<template>
    <page-controls>
        <template v-slot:left>
            <div class="w-full grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="active = $event">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find</span>
                        <vx-badge v-if="filtersCount > 0" :borders="false" size="xs" @click.stop="reset">
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="flex flex-col gap-4">
                            <div>
                                <filter-label :canReset="!!filters.search" @reset="filters.search = undefined">
                                    Keyword Search
                                </filter-label>

                                <vx-input
                                    type="text"
                                    data-test="controls-query"
                                    placeholder="Search"
                                    maxlength="80"
                                    size="lg"
                                    v-model="filters.search"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button type="button" size="lg" color="muted-light" @click="cancel"
                                    >Cancel</vx-button
                                >
                                <vx-button data-test="controls-apply" size="lg" type="submit" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        v-model="filters.sort_field"
                        @update:modelValue="apply"
                        :options="[
                            {
                                value: 'id',
                                label: 'Sort by: Created',
                            },
                            {
                                value: 'friendly_name',
                                label: 'Sort by: Name',
                            },
                        ]"
                    ></vx-select>
                    <vx-button
                        @click.prevent="
                            filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                            apply();
                        "
                        type="button"
                        color="white"
                        shadow
                        data-test="sort-direction"
                        :aria-label="`Sort ${
                            props.modelValue.sort_dir === 'asc' ? 'ascending' : 'descending'
                        }. Press to toggle sort direction.`"
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="props.modelValue.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>

        <template v-if="isImpersonatingOrPlatformAdmin" v-slot:right>
            <vx-button
                :href="`/settings/teams/${teamId}/phone-number-pools/new`"
                size="lg"
                data-test="add-new"
                color="primary"
            >
                New Phone Pool
                <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
            </vx-button>
        </template>
    </page-controls>
</template>

<script setup>
import PageControls from '~/components/general/PageControls.vue';
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCircleCheck,
    faCirclePlus,
    faMagnifyingGlass,
    faCircleXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { VxBadge, VxButton, VxButtonGroup, VxFilterable, VxInput, VxSelect } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { computed, nextTick, ref, watch } from 'vue';
import { pluralized } from '~/components/filters';
import { filtersKey } from '~/composables/useCursorPagination';

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;
const isImpersonatingOrPlatformAdmin = Spark.isImpersonating || Spark.isPlatformAdmin;
const filters = ref({});

watch(
    () => filtersKey(props.modelValue),
    () => {
        filters.value = { ...props.modelValue };
    },
    {
        immediate: true,
    }
);

const emit = defineEmits(['update:modelValue', 'apply', 'reset']);
const active = ref(false);

const filtersCount = computed(() => {
    let count = 0;

    count += props.modelValue.search?.length ? 1 : 0;

    return count;
});

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue };
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};
</script>
